import { useEffect, useState, useRef } from "react";
import { Calculator } from "lucide-react";
import { getTool } from "@/lib/tools/tool-config";
import { estimateClosingCosts } from "@/lib/tools/scoring";
import { formatBand } from "@/lib/tools/ranges";
import { disclaimersFor } from "@/lib/tools/disclaimers";
import { track } from "@/lib/analytics";

import ToolPageShell from "@/components/tools/ToolPageShell";
import {
    FormSection,
    PillSingleSelect,
    NumberField,
    TextField,
} from "@/components/tools/ToolFormFields";
import {
    PrimaryCtaBlock,
    DisclaimerList,
    RelatedArticles,
    CostSafeNote,
    ConcernBadge,
} from "@/components/tools/ToolResultShell";
import EmailResultsForm from "@/components/tools/EmailResultsForm";
import SoftPaywallGate from "@/components/tools/SoftPaywallGate";
import useCalcSoftPaywall from "@/components/tools/useCalcSoftPaywall";
import SaveMyAnswersForm from "@/components/tools/SaveMyAnswersForm";
import LenderEstimateForm from "@/components/tools/LenderEstimateForm";
import MarketTierBadge from "@/components/tools/MarketTierBadge";
import { useResumeFromUrl } from "@/components/tools/useResumeFromUrl";

const TOOL_SLUG = "closing-cost-calculator";
const TOOL = getTool(TOOL_SLUG);

const LOAN_TYPES = [
    { value: "conventional", label: "Conventional" },
    { value: "fha", label: "FHA" },
    { value: "va", label: "VA" },
    { value: "cash", label: "Cash" },
    { value: "unsure", label: "Unsure" },
];

const FAQS = [
    {
        q: "How much are closing costs for the buyer?",
        a: "Typically 2–5% of the home price for financed deals, and 0.5–1.5% for cash. Loan type, points, and seller concessions all shift the final number.",
    },
    {
        q: "Are FHA closing costs higher than conventional?",
        a: "Usually slightly. FHA loans carry an upfront mortgage insurance premium (UFMIP) — around 1.75% of the loan amount — which is most often rolled in but still hits your bottom line.",
    },
    {
        q: "Can the seller pay my closing costs?",
        a: "Often yes, up to a lender cap that varies by loan type (roughly 6% conventional, 4% VA, plus product-specific rules). This tool clips the credit to the cap.",
    },
    {
        q: "Are prepaids the same as closing costs?",
        a: "Lenders bundle them on the Closing Disclosure, but conceptually they're separate: prepaids fund your escrow for taxes and insurance. We include a 0.4–0.8% band by default.",
    },
];

const INTRO =
    "Rule-based estimate of buyer closing costs. Adjusts for loan type, points buy-down, seller credits, and prepaid escrow. Not a Loan Estimate — use it for planning.";

export default function ClosingCostCalculator() {
    const [homePrice, setHomePrice] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [loanType, setLoanType] = useState("conventional");
    const [sellerCreditPct, setSellerCreditPct] = useState("");
    const [pointsBuydownPct, setPointsBuydownPct] = useState("");
    const [includePrepaids, setIncludePrepaids] = useState(true);
    const [result, setResult] = useState(null);
    const { gated, tryShowResult, unlock } = useCalcSoftPaywall();
    const resultRef = useRef(null);

    useEffect(() => {
        track("tool_started", { tool_name: TOOL_SLUG });
    }, []);

    // Restore from /tools/closing-cost-calculator?resume=<token>
    useResumeFromUrl(TOOL_SLUG, ({ inputs, result: restored, zipCode: z }) => {
        if (inputs) {
            if (inputs.homePrice != null) setHomePrice(String(inputs.homePrice));
            if (inputs.loanType) setLoanType(inputs.loanType);
            if (inputs.sellerCreditPct != null)
                setSellerCreditPct(String(inputs.sellerCreditPct));
            if (inputs.pointsBuydownPct != null)
                setPointsBuydownPct(String(inputs.pointsBuydownPct));
            if (typeof inputs.includePrepaids === "boolean")
                setIncludePrepaids(inputs.includePrepaids);
        }
        if (z) setZipCode(z);
        if (restored) setResult(restored);
    });

    const onSubmit = (e) => {
        e.preventDefault();
        const out = estimateClosingCosts({
            homePrice: Number(homePrice) || 0,
            loanType,
            sellerCreditPct: Number(sellerCreditPct) || 0,
            pointsBuydownPct: Number(pointsBuydownPct) || 0,
            includePrepaids,
        });
        if (!tryShowResult(out, setResult)) return;
        track("tool_completed", {
            tool_name: TOOL_SLUG,
            result_level: out.level,
            zip_present: !!zipCode,
            completed: true,
        });
        setTimeout(() => {
            resultRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            track("tool_result_viewed", {
                tool_name: TOOL_SLUG,
                result_level: out.level,
            });
        }, 50);
    };

    return (
        <ToolPageShell tool={TOOL} intro={INTRO} faqs={FAQS}>
            {gated && (
                <SoftPaywallGate toolSlug={TOOL_SLUG} onUnlock={unlock} />
            )}
            {!result && !gated && (
                <form
                    onSubmit={onSubmit}
                    className="bl-card p-6 sm:p-8 space-y-7"
                    data-testid="closing-cost-form"
                >
                    <FormSection title="The deal">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <NumberField
                                label="Home price"
                                value={homePrice}
                                onChange={setHomePrice}
                                prefix="$"
                                placeholder="500,000"
                                testid="home-price"
                            />
                            <TextField
                                label="ZIP code (optional)"
                                value={zipCode}
                                onChange={setZipCode}
                                placeholder="e.g. 28801"
                                maxLength={5}
                                testid="zip-code"
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Loan type">
                        <PillSingleSelect
                            options={LOAN_TYPES}
                            value={loanType}
                            onChange={setLoanType}
                            testidPrefix="loan-type"
                        />
                    </FormSection>

                    <FormSection title="Adjustments (optional)">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <NumberField
                                label="Seller credit (% of price)"
                                value={sellerCreditPct}
                                onChange={setSellerCreditPct}
                                suffix="%"
                                placeholder="2"
                                testid="seller-credit-pct"
                            />
                            <NumberField
                                label="Points buy-down (%)"
                                value={pointsBuydownPct}
                                onChange={setPointsBuydownPct}
                                suffix="%"
                                placeholder="0"
                                testid="points-buydown-pct"
                            />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-bl-muted mt-3">
                            <input
                                type="checkbox"
                                checked={includePrepaids}
                                onChange={(e) =>
                                    setIncludePrepaids(e.target.checked)
                                }
                                data-testid="include-prepaids"
                            />
                            Include prepaid escrow (taxes + insurance buffer)
                        </label>
                    </FormSection>

                    <button
                        type="submit"
                        className="bl-btn-primary w-full sm:w-auto justify-center gap-2"
                        data-testid="closing-cost-submit"
                    >
                        <Calculator className="h-4 w-4" />
                        Estimate closing costs
                    </button>
                </form>
            )}

            {result && (
                <div ref={resultRef} className="space-y-6">
                    <div className="bl-card p-6 sm:p-8 space-y-5 reveal-mount"
                        data-testid="closing-cost-result"
                    >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <ConcernBadge level={result.level} />
                            {result.sellerCreditApplied > 0 && (
                                <span className="text-xs text-bl-muted">
                                    Seller credit applied: $
                                    {result.sellerCreditApplied.toLocaleString()}
                                </span>
                            )}
                        </div>

                        <MarketTierBadge zipCode={zipCode} />

                        <div className="rounded-lg border border-bl bg-bl-bg p-4">
                            <div className="text-xs uppercase tracking-wide text-bl-muted">
                                Estimated buyer closing costs
                            </div>
                            <div
                                className="mt-1 font-mono text-3xl font-semibold text-bl"
                                data-testid="closing-cost-range"
                            >
                                {formatBand(result.range)}
                            </div>
                            <p className="mt-1.5 text-xs text-bl-muted">
                                Roughly{" "}
                                {(result.sharePctHi * 100).toFixed(1)}% of
                                home price at the upper end.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-bl mb-2">
                                Notes
                            </h3>
                            <ul className="space-y-1.5">
                                {result.tips.map((t, i) => (
                                    <li
                                        key={t}
                                        className="text-sm text-bl-muted flex items-start gap-2"
                                    >
                                        <span className="text-bl-primary mt-1">
                                            •
                                        </span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <PrimaryCtaBlock toolSlug={TOOL_SLUG} includeClosingCta />
                    <LenderEstimateForm
                        homePrice={Number(homePrice) || 0}
                        zipCode={zipCode}
                        loanType={loanType}
                        estimateSummary={{
                            level: result.level,
                            range: result.range,
                            sellerCreditApplied: result.sellerCreditApplied,
                            sharePctHi: result.sharePctHi,
                        }}
                    />
                    <CostSafeNote />
                    <SaveMyAnswersForm
                        toolSlug={TOOL_SLUG}
                        zipCode={zipCode}
                        inputs={{
                            homePrice: Number(homePrice) || 0,
                            loanType,
                            sellerCreditPct: Number(sellerCreditPct) || 0,
                            pointsBuydownPct: Number(pointsBuydownPct) || 0,
                            includePrepaids,
                        }}
                        result={{
                            level: result.level,
                            range: result.range,
                        }}
                    />
                    <EmailResultsForm
                        toolSlug={TOOL_SLUG}
                        zipCode={zipCode}
                        resultSummary={{
                            level: result.level,
                            range: result.range,
                        }}
                    />
                    <DisclaimerList disclaimers={disclaimersFor(TOOL_SLUG)} />
                    <RelatedArticles
                        slugs={TOOL?.relatedArticles || []}
                        toolSlug={TOOL_SLUG}
                    />
                </div>
            )}
        </ToolPageShell>
    );
}
